import { handleHttpRequest, http, type HttpResponse } from 'interfaces'
import type {
  ExecOrgRef,
  ExecOrgUrl,
  ExecutiveOrganization,
  ExecutiveOrganizationBasicInfo
} from 'services/executives/executiveOrgLookupService'

export interface CreateExecutiveOrgRequest {
  name: string
  code: string | null
  role: { id: number }
  state: { id: number } | null
  parentOrg: { id: number } | null
  // Optional default contact captured at create (TI-4053), already in the flat
  // API shape produced by `execOrgAddressToApi`.
  address?: Record<string, unknown>
}

export type UpdateExecutiveOrgRequest =
  Pick<ExecOrgRef, 'name' | 'code'> & { responsibilities: number[], role?: { id: number } }

export interface MoveExecutiveOrgRequest {
  role: { id: number }
  parentOrg: { id: number }
}

export async function createExecutiveOrganization (
  payload: CreateExecutiveOrgRequest
): Promise<HttpResponse<ExecutiveOrganization>> {
  return await handleHttpRequest(http.post('/api/v1/us/executives/orgs/', payload))
}

export async function updateExecutiveOrganization (
  orgId: number,
  payload: UpdateExecutiveOrgRequest
): Promise<HttpResponse<ExecutiveOrganizationBasicInfo>> {
  return await handleHttpRequest(http.patch(`/api/v1/us/executives/orgs/${orgId}/`, payload))
}

export async function moveExecutiveOrganization (
  orgId: number,
  payload: MoveExecutiveOrgRequest
): Promise<HttpResponse<ExecutiveOrganizationBasicInfo>> {
  return await handleHttpRequest(http.patch(`/api/v1/us/executives/orgs/${orgId}/move/`, payload))
}

export async function retireAllExecutiveOrgPositions (
  orgId: number
): Promise<HttpResponse<{ retiredCount: number }>> {
  return await handleHttpRequest(
    http.post(`/api/v1/us/executives/orgs/${orgId}/retire-positions/`, {})
  )
}

/**
 * DELETE returns a wrapped 200 on success, 403 when the manager is not on the
 * delete whitelist, and 400 with a guard message otherwise — all in the standard
 * ResponseApi envelope, so the UI branches on `status` (403 vs 400) — see ADR-014.
 */
export async function deleteExecutiveOrganization (
  orgId: number,
  cascade = false
): Promise<HttpResponse<null>> {
  const query = cascade ? '?cascade=true' : ''
  return await handleHttpRequest(http.delete(`/api/v1/us/executives/orgs/${orgId}/${query}`))
}

export async function listExecutiveOrgUrls (orgId: number): Promise<ExecOrgUrl[]> {
  const response = await http.get<ExecOrgUrl[]>(`/api/v1/us/executives/orgs/${orgId}/urls/`)
  return response.data
}

export async function createExecutiveOrgUrl (orgId: number, url: ExecOrgUrl): Promise<HttpResponse<ExecOrgUrl>> {
  return await handleHttpRequest(http.post(`/api/v1/us/executives/orgs/${orgId}/urls/`, url))
}

export async function updateExecutiveOrgUrl (orgId: number, urlId: number, value: string): Promise<HttpResponse<ExecOrgUrl>> {
  return await handleHttpRequest(http.patch(`/api/v1/us/executives/orgs/${orgId}/urls/${urlId}/`, { value }))
}

export async function deleteExecutiveOrgUrl (orgId: number, urlId: number): Promise<boolean> {
  return await http.delete(`/api/v1/us/executives/orgs/${orgId}/urls/${urlId}/`)
    .then(() => true)
    .catch(() => false)
}
